"use client";
//import node module libraries
import { Fragment } from "react";

import {
  BrainCircuit,
  BrainCog,
  ClipboardList,
  SquarePen,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useRouter } from "next/navigation";

//import custom shadcn UI components
import { Button } from "@/components/ui/button";

// import server action
import { getSampleData } from "@/lib/api-client";
import { useToggle } from "@/hooks/useToggle";
import { v4 as uuid } from "uuid";
import useServerAction from "@/hooks/useServerAction";
import { useParams } from "next/navigation";
import AddTableSchema from "./AddTableSchema";
import BrandLogo from "@/components/common/BrandLogo";

import ChatHistoryList from "./ChatHistoryList";
import useChat from "@/hooks/useChat";
import { getChatList } from "@/lib/api-client";
import type { ChatMessage, TableData } from "@/types";

interface ChatInnerSidebarProps {
  chatHistory: any;
}

interface Chat {
  chat_id: string;
  updated_at: string;
  chat_history: { description: string }[];
}

interface GetSampleDataResponse {
  result?: {
    description?: string;
    id?: string;
    role?: string;
    title?: string;
    table_data?: TableData[];
    chat_id?: string;
  };
}

const ChatInnerSidebar: React.FC<ChatInnerSidebarProps> = ({ chatHistory }) => {
  const { chat_id } = useParams();

  const {
    isToggled: isColumnSchema,
    handleOpen: openSchema,
    handleClose: closeSchema,
  } = useToggle();

  const { fetchTemplateQuestion, fetchChatSuggestion } = useServerAction();

  const {
    chatEndFn,
    chatStartedFn,
    activeChatId,
    handleAddChat,
    handleCurrentChatId,
    handleResetAllChat,
    handleSetNewChatIntent,
    handleSampleData,
  } = useChat();

  const router = useRouter();

  const {
    data = [],
    refetch,
    isFetching,
    isLoading,
  } = useQuery<Chat[]>({
    queryKey: ["chat-history"],
    queryFn: async () => {
      const { chats } = await getChatList();
      return (
        (chats || []).map((chat: any) => ({
          chat_id: chat.chat_id,
          updated_at: chat.updated_at,
          chat_history: chat.chat_history || [],
        })) || []
      );
    },
    initialData: chatHistory,
    staleTime: 2 * 60 * 1000,
    retry: 1,
    refetchOnMount: false,
  });

  const { mutate: getSampleDataFn } = useMutation<
    GetSampleDataResponse,
    Error,
    string
  >({
    mutationFn: async (data: string) => {
      const response = await getSampleData(data);
      return response as GetSampleDataResponse;
    },

    onSuccess: async (data) => {
      const { result } = data;

      const chatMessage = {
        id: result?.id ?? uuid(),
        role: result?.role ?? "assistant",
        description: result?.description ?? "",
        title: result?.title,
        table_data: result?.table_data,
      };

      handleAddChat(chatMessage as ChatMessage);
      handleCurrentChatId(result?.chat_id ?? null);
    },
    onSettled: () => {
      if (!chat_id && activeChatId) {
        router.push(`chatbot/${activeChatId}`);
      }
      refetch();
      chatEndFn();
    },
  });

  const getSampleDataView = async () => {
    chatStartedFn();

    if (activeChatId) {
      getSampleDataFn(activeChatId);
      handleSampleData(true);
    } else {
      getSampleDataFn("");
      handleSampleData(true);
    }
  };

  // Stable ids so button keys don't change on re-render (avoids needing double-click to navigate)
  const quickMenu = [
    {
      id: "new-chat",
      title: "New Chat",
      icon: <SquarePen size={20} />,
      onClick: () => {
        handleSetNewChatIntent(true);
        handleResetAllChat();
        // Defer navigation so it runs after React commits state; avoids needing double-click
        setTimeout(() => router.replace("/chatbot"), 0);
      },
    },
    {
      id: "ai-suggested-questions",
      title: "AI-Suggested Questions",
      icon: <BrainCog size={20} />,
      onClick: fetchChatSuggestion,
    },
    {
      id: "ai-suggested-templates",
      title: "AI-Suggested Templates",
      icon: <BrainCircuit size={20} />,
      onClick: fetchTemplateQuestion,
    },
    {
      id: "column-description",
      title: "Column Description",
      icon: <ClipboardList size={20} />,
      onClick: openSchema,
    },
  ];
  return (
    <Fragment>
      <div
        className={`w-1/5 p-2 h-full flex-shrink-0 flex flex-col justify-between bg-zinc-50 dark:bg-[#1E1F20]`}
      >
        <div className="flex flex-col justify-start gap-2 h-full">
          {/* <div className="p-3 ">
            <BrandLogo />
          </div> */}
          <div className="flex items-start justify-start flex-col gap-1 max-h-fit relative">
            {quickMenu.map((item) => (
              <div key={item.id} className="w-full">
                <Button
                  variant={"ghost"}
                  className="w-full flex gap-2 items-center justify-start font-medium"
                  onClick={() => {
                    item.onClick();
                  }}
                >
                  <span>{item.icon}</span>
                  <p className="hidden md:block md:text-xs lg:text-sm">
                    {item.title}
                  </p>
                </Button>
              </div>
            ))}
          </div>

          <ChatHistoryList
            initialChats={data}
            refetch={refetch}
            isLoading={isLoading || isFetching}
          />
        </div>
      </div>

      <AddTableSchema isOpen={isColumnSchema} onClose={closeSchema} />
    </Fragment>
  );
};

export default ChatInnerSidebar;
